// src/features/gatherings/components/gathering-detail/TransferOwnershipDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Avatar,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Gathering, FabrikUser } from '../../../../types';
import { fetchUsers } from '../../../../services/api/endpoints/users';
import { transferGatheringOwnership } from '../../../../services/api/endpoints/gatherings';

interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  gathering: Gathering;
  onSuccess: () => void;
  token: string;
}

const TransferOwnershipDialog: React.FC<TransferOwnershipDialogProps> = ({
  open,
  onClose,
  gathering,
  onSuccess,
  token
}) => {
  const [selectedUser, setSelectedUser] = useState<FabrikUser | null>(null);
  const [users, setUsers] = useState<FabrikUser[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch users when search text changes
  useEffect(() => {
    if (!open) return;

    const delayDebounce = setTimeout(() => {
      if (searchText.length >= 2) {
        fetchUsersData(searchText);
      } else {
        setUsers([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchText, open]);

  const fetchUsersData = async (query: string) => {
    setIsLoadingUsers(true);
    try {
      const response = await fetchUsers(
        token,
        1,
        query, // email filter
        undefined, // userId
        false, // includeUsageData
        false, // membersOnly
        'last_name',
        'asc',
        false // includeCaasData
      );
      setUsers(response.items || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUser) return;

    setIsTransferring(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await transferGatheringOwnership(token, gathering.id, selectedUser.id);
      setSuccessMessage(`Successfully transferred ownership to ${selectedUser.display_name || selectedUser.email}`);
      
      // Wait a moment to show success message
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error transferring ownership:', err);
      setError(err instanceof Error ? err.message : 'Failed to transfer ownership');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleClose = () => {
    if (isTransferring) return;
    setSelectedUser(null);
    setSearchText('');
    setUsers([]);
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  const getUserDisplayName = (user: FabrikUser) => {
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon />
          <Typography variant="h6">Transfer Gathering Ownership</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Current Owner */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Current Owner
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                fontSize: '0.875rem',
                bgcolor: 'primary.main'
              }}
            >
              {gathering.owner?.display_name?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {gathering.owner?.display_name || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {gathering.owner?.id}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* New Owner Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            New Owner
          </Typography>
          <Autocomplete
            options={users}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            inputValue={searchText}
            onInputChange={(_, newInputValue) => setSearchText(newInputValue)}
            getOptionLabel={(option) => getUserDisplayName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingUsers}
            noOptionsText={searchText.length < 2 ? "Type at least 2 characters to search" : "No users found"}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by email or name..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '0.875rem',
                      bgcolor: 'secondary.main'
                    }}
                  >
                    {option.display_name?.charAt(0) || option.email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {getUserDisplayName(option)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Warning */}
        {selectedUser && !successMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will transfer ownership of "{gathering.title}" to {getUserDisplayName(selectedUser)}. 
            The current owner will lose their ability to manage this gathering.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={isTransferring}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleTransfer}
          variant="contained"
          color="primary"
          disabled={!selectedUser || isTransferring}
          startIcon={isTransferring ? <CircularProgress size={20} /> : <SwapHorizIcon />}
        >
          {isTransferring ? 'Transferring...' : 'Transfer Ownership'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
